/**
 * Bilingual copy for Phase-A borrower-portal surfaces.
 * Same copy-dict pattern as LOGIN_COPY in CustomerLoginPage.tsx — EN/ES parity
 * is enforced by portalCopy.test.ts.
 */

export type PortalLanguage = 'en' | 'es';

export interface PortalCopy {
  scheduleTitle: string;
  scheduleEmpty: string;
  scheduleCaution: string;
  colPeriod: string;
  colDueDate: string;
  colAmount: string;
  colPrincipal: string;
  colInterest: string;
  colStatus: string;
  statusPaid: string;
  statusPartial: string;
  statusUnpaid: string;
  payoffTitle: string;
  payoffDateLabel: string;
  payoffGetQuote: string;
  payoffBalance: string;
  payoffPerDiem: string;
  payoffEstimated: string;
  payoffUnavailable: string;
  payoffDisclaimer: string;
  statementsTitle: string;
  statementsMonthLabel: string;
  statementsDownload: string;
  receiptButton: string;
  lateBalance: string;
  lastPayment: string;
  promiseChip: string;
  requestsTitle: string;
  requestsNew: string;
  requestsEmpty: string;
  requestTypeLabel: string;
  requestTypeContact: string;
  requestTypeDispute: string;
  requestTypePromise: string;
  requestSubject: string;
  requestMessage: string;
  requestPromiseDate: string;
  requestSubmit: string;
  requestCancel: string;
  requestSubmitted: string;
  requestStatusOpen: string;
  requestStatusInReview: string;
  requestStatusResolved: string;
  requestStatusClosed: string;
  requestsPromiseCta: string;
  requestSubmittedLabel: string;
  requestPromiseDateLabel: string;
  loadError: string;
  threadViewConversation: string;
  threadHideConversation: string;
  threadYou: string;
  threadDealership: string;
  threadHistorical: string;
  threadReplyPlaceholder: string;
  threadSend: string;
  threadReplyError: string;
  threadArchive: string;
  threadUnarchive: string;
  threadShowArchived: string;
  threadLoadOlder: string;
  threadUnreadBadge: string;
}

export const PORTAL_COPY: Record<PortalLanguage, PortalCopy> = {
  en: {
    scheduleTitle: 'Payment Plan',
    scheduleEmpty:
      'Your payment plan is not available online. Contact the dealership for your schedule.',
    scheduleCaution:
      'Some payments could not be matched to installments; status shown may be incomplete. Contact the dealership with questions.',
    colPeriod: '#',
    colDueDate: 'Due Date',
    colAmount: 'Amount',
    colPrincipal: 'Principal',
    colInterest: 'Interest',
    colStatus: 'Status',
    statusPaid: 'Paid',
    statusPartial: 'Partial',
    statusUnpaid: 'Unpaid',
    payoffTitle: 'Payoff Estimate',
    payoffDateLabel: 'Payoff date',
    payoffGetQuote: 'Get Estimate',
    payoffBalance: 'Current balance',
    payoffPerDiem: 'Daily interest',
    payoffEstimated: 'Estimated payoff',
    payoffUnavailable:
      'Daily interest is not available online for this account; the amount shown is your current balance only.',
    payoffDisclaimer: 'Estimate only. Contact the dealership for an official payoff amount.',
    statementsTitle: 'Statements',
    statementsMonthLabel: 'Statement month',
    statementsDownload: 'Download PDF',
    receiptButton: 'Receipt',
    lateBalance: 'Late Balance',
    lastPayment: 'Last Payment',
    promiseChip: 'Promise to pay',
    requestsTitle: 'Messages & Requests',
    requestsNew: 'Contact the Dealership',
    requestsEmpty: 'No requests yet.',
    requestTypeLabel: 'What do you need?',
    requestTypeContact: 'Contact / question',
    requestTypeDispute: 'Dispute a charge or payment',
    requestTypePromise: 'Promise to pay',
    requestSubject: 'Subject',
    requestMessage: 'Message',
    requestPromiseDate: 'I can pay on',
    requestSubmit: 'Send',
    requestCancel: 'Cancel',
    requestSubmitted: 'Your request was sent to the dealership.',
    requestStatusOpen: 'Open',
    requestStatusInReview: 'In review',
    requestStatusResolved: 'Resolved',
    requestStatusClosed: 'Closed',
    requestsPromiseCta: 'Promise to Pay',
    requestSubmittedLabel: 'Submitted',
    requestPromiseDateLabel: 'Promise date',
    loadError: 'Unable to load this section. Please try again later.',
    threadViewConversation: 'View conversation',
    threadHideConversation: 'Hide conversation',
    threadYou: 'You',
    threadDealership: 'Dealership',
    threadHistorical: 'Historical — original author not recorded',
    threadReplyPlaceholder: 'Write a reply…',
    threadSend: 'Send',
    threadReplyError: 'Unable to send your reply. Please try again.',
    threadArchive: 'Archive',
    threadUnarchive: 'Unarchive',
    threadShowArchived: 'Show archived',
    threadLoadOlder: 'Load older messages',
    threadUnreadBadge: 'Unread',
  },
  es: {
    scheduleTitle: 'Plan de Pagos',
    scheduleEmpty:
      'Tu plan de pagos no esta disponible en linea. Contacta al concesionario para tu calendario.',
    scheduleCaution:
      'Algunos pagos no se pudieron asociar a las cuotas; el estado mostrado puede estar incompleto. Contacta al concesionario si tienes dudas.',
    colPeriod: '#',
    colDueDate: 'Fecha de Pago',
    colAmount: 'Monto',
    colPrincipal: 'Capital',
    colInterest: 'Interes',
    colStatus: 'Estado',
    statusPaid: 'Pagado',
    statusPartial: 'Parcial',
    statusUnpaid: 'Pendiente',
    payoffTitle: 'Estimado de Liquidacion',
    payoffDateLabel: 'Fecha de liquidacion',
    payoffGetQuote: 'Obtener Estimado',
    payoffBalance: 'Saldo actual',
    payoffPerDiem: 'Interes diario',
    payoffEstimated: 'Liquidacion estimada',
    payoffUnavailable:
      'El interes diario no esta disponible en linea para esta cuenta; el monto mostrado es solo tu saldo actual.',
    payoffDisclaimer:
      'Es un monto estimado. Contacta al concesionario para el monto oficial de liquidacion.',
    statementsTitle: 'Estados de Cuenta',
    statementsMonthLabel: 'Mes del estado de cuenta',
    statementsDownload: 'Descargar PDF',
    receiptButton: 'Recibo',
    lateBalance: 'Saldo Atrasado',
    lastPayment: 'Ultimo Pago',
    promiseChip: 'Promesa de pago',
    requestsTitle: 'Mensajes y Solicitudes',
    requestsNew: 'Contactar al Concesionario',
    requestsEmpty: 'Aun no hay solicitudes.',
    requestTypeLabel: 'Que necesitas?',
    requestTypeContact: 'Contacto / pregunta',
    requestTypeDispute: 'Disputar un cargo o pago',
    requestTypePromise: 'Promesa de pago',
    requestSubject: 'Asunto',
    requestMessage: 'Mensaje',
    requestPromiseDate: 'Puedo pagar el',
    requestSubmit: 'Enviar',
    requestCancel: 'Cancelar',
    requestSubmitted: 'Tu solicitud fue enviada al concesionario.',
    requestStatusOpen: 'Abierta',
    requestStatusInReview: 'En revision',
    requestStatusResolved: 'Resuelta',
    requestStatusClosed: 'Cerrada',
    requestsPromiseCta: 'Promesa de Pago',
    requestSubmittedLabel: 'Enviado',
    requestPromiseDateLabel: 'Fecha de promesa',
    loadError: 'No se pudo cargar esta seccion. Intenta de nuevo mas tarde.',
    threadViewConversation: 'Ver conversacion',
    threadHideConversation: 'Ocultar conversacion',
    threadYou: 'Tu',
    threadDealership: 'Concesionario',
    threadHistorical: 'Historial — autor original no registrado',
    threadReplyPlaceholder: 'Escribe una respuesta…',
    threadSend: 'Enviar',
    threadReplyError: 'No se pudo enviar tu respuesta. Intenta de nuevo.',
    threadArchive: 'Archivar',
    threadUnarchive: 'Desarchivar',
    threadShowArchived: 'Mostrar archivadas',
    threadLoadOlder: 'Cargar mensajes anteriores',
    threadUnreadBadge: 'No leido',
  },
};
